import React, { useCallback, useRef, useState, useEffect } from 'react';
import styled from 'astroturf/react';

import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import routes from 'js/core_pre_webpack/routes';
import {
  trackClick,
  Options as ClickTrackingOptions,
} from 'js/utils/trackClick';
import { consoleError } from 'js/utils/console';

import { Input } from 'js/design_system/forms/Input';
import { Body } from 'js/design_system/Body';
import { Button } from 'js/design_system/Button';

import { TitleText, BodyText } from './common';
import {
  ModalPage,
  RegistrationParams,
  EmailLoginPageConfigurableText,
} from '../types';
import { BackButton } from '../components/BackButton';
import { NewLine } from '../components/NewLine';
import { CenteredDiv } from '../components/CenteredDiv';
import { VerticalSpacer } from '../components/VerticalSpacer';
import { ModalError } from '../components/ModalError';

const Link = styled(Body).attrs({
  size: 'sm',
})`
  cursor: pointer;
  color: var(--neutrals-90, #535366);
  text-align: center;

  font-weight: 500;
  &:hover {
    color: var(--primary-brand, #0645b1);
    text-decoration: underline;
  }
  &:focus {
    color: var(--hover, #0c3b8d);
    text-decoration: underline;
  }
`;

const wrongPasswordErrorText = 'That is not correct. Please try again.';
const resetPasswordErrorText =
  'Failed to reset password. Please try again later.';

type Props = {
  userFirstName: string;
  userEmail: string;
  onSuccess: () => void;
  setPage: React.Dispatch<React.SetStateAction<ModalPage>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  show: boolean;
  clickTrackingContext?: Partial<ClickTrackingOptions>;
  text: EmailLoginPageConfigurableText;
  registrationParams: RegistrationParams;
  largeBackButton?: boolean;
};

export const EmailLoginPage: React.FC<Props> = (props) => {
  const {
    userFirstName,
    userEmail,
    onSuccess,
    setPage,
    setLoading,
    show,
    clickTrackingContext,
    text,
    registrationParams,
  } = props;

  const passwordRef = useRef<HTMLInputElement>(null);
  const [passwordErrorText, setPasswordErrorText] = useState('');
  const [showModalError, setShowModalError] = useState(false);
  const [modalErrorText, setModalErrorText] = useState<string | undefined>(
    undefined,
  );
  const resetPassword = useCallback(async () => {
    setShowModalError(false);
    setModalErrorText(undefined);
    setLoading(true);
    trackClick({
      source: 'login_modal_email_login_reset_password_button',
      ...clickTrackingContext,
    });
    const resp = await fetchWithCsrf(
      routes.api_v0_reset_password_path({ subdomain_param: 'api' }),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userEmail,
        }),
      },
    );
    try {
      const respBody = await resp.json();
      if (respBody.success) {
        setLoading(false);
        setPage('password-reset');
      } else {
        setLoading(false);
        setShowModalError(true);
        setModalErrorText(respBody.message || resetPasswordErrorText);
      }
    } catch (e) {
      setLoading(false);
      consoleError(e as Error);
      setShowModalError(true);
      setModalErrorText(resetPasswordErrorText);
    }
  }, [setLoading, clickTrackingContext, userEmail, setPage]);

  const handleSubmit = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      setPasswordErrorText('');
      setShowModalError(false);
      setModalErrorText(undefined);
      setLoading(true);
      passwordRef.current?.setCustomValidity('');

      trackClick({
        source: 'login_modal_email_login_submit_button',
        ...clickTrackingContext,
      });

      const resp = await fetchWithCsrf(
        routes.api_v0_login_path({ subdomain_param: 'api' }),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            login_email: userEmail,
            password: passwordRef.current?.value,
            ...registrationParams,
          }),
        },
      );
      try {
        const respBody = await resp.json();

        if (respBody.success) {
          onSuccess();
        } else {
          if (respBody.message === 'Invalid email address or password.') {
            setPasswordErrorText(wrongPasswordErrorText);
            passwordRef.current?.setCustomValidity(wrongPasswordErrorText);
            passwordRef.current?.checkValidity();
          } else {
            // It varies whether the response body for unsuccessful login is appropriate to expose
            // to the user in the UI as-is. So check for specific known-good cases before using
            // respBody.message
            if (
              resp.status === 429 ||
              respBody.message ===
                'Please check your email to verify this login' ||
              respBody.message ===
                'Log in is currently disabled while we undergo important maintenance.'
            ) {
              setModalErrorText(respBody.message);
            }
            consoleError(respBody);
          }
          setShowModalError(true);
          setLoading(false);
        }
      } catch (e) {
        consoleError(e as Error);
        setShowModalError(true);
        setLoading(false);
      }
    },
    [
      clickTrackingContext,
      registrationParams,
      onSuccess,
      setLoading,
      userEmail,
    ],
  );

  useEffect(() => {
    if (show && passwordRef.current) {
      passwordRef.current.focus();
    }
  }, [show]);

  return (
    <div style={{ display: show ? 'initial' : 'none' }}>
      <BackButton
        onClick={() => {
          trackClick({
            source: 'login_modal_email_login_back_button',
            ...clickTrackingContext,
          });
          setPage('email-entry');
        }}
      />
      <TitleText>Hi, {userFirstName}</TitleText>
      <BodyText>{text.subheader}</BodyText>
      <ModalError show={showModalError} errorMessage={modalErrorText} />
      <VerticalSpacer height={32} />
      <Input
        label="Password"
        name="current-password"
        ref={passwordRef}
        type="password"
        autoComplete="current-password"
        required={show}
        placeholder="Your password"
        hasError={!!passwordErrorText}
        errors={passwordErrorText ? [passwordErrorText] : []}
      />
      <NewLine />
      <VerticalSpacer height={16} />
      <CenteredDiv>
        <Link onClick={resetPassword}>Reset password</Link>
      </CenteredDiv>
      <VerticalSpacer height={40} />
      <CenteredDiv>
        <Button
          size="lg"
          type="submit"
          disabled={!show}
          iconRight="arrow_forward"
          onClick={handleSubmit}
        >
          {opener === 'download pdf button' ? 'Start download' : 'Log in'}
        </Button>
      </CenteredDiv>
    </div>
  );
};
